import React, { useState, useCallback, useEffect } from "react";
import { AutoComplete, Form, Input, Row, Col, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { capitalizeFirstLetter, debounce } from "../../../helpers/utility";
import { getPractitionerSearchApi } from "../../../services/receptionist.services";
import { validateConfig } from "../../../config/validate.config";

const buildSearchOptions = (data, type) =>
  data.map((options) => ({
    value: `${capitalizeFirstLetter(
      options?.first_name
    )} ${capitalizeFirstLetter(
      options?.last_name
    )} (${capitalizeFirstLetter(options?.role)})`,
    key: type ? options?.user__id : options?.id,
    label: (
      <Row>
        <Col span={10}>
          <span>
            {capitalizeFirstLetter(options?.first_name)}{" "}
            {capitalizeFirstLetter(options?.last_name)}
            {"  "}
            {<Tag color="blue">{options?.role}</Tag>}
          </span>
        </Col>
      </Row>
    ),
  }));

/** Form-connected practitioner picker — use inside Form.Item (value = practitioner id). */
export const PractitionerFormInput = ({
  value,
  onChange,
  facilityId,
  initialDisplayName = "",
}) => {
  const [options, setOptions] = useState([]);
  const [displayValue, setDisplayValue] = useState(initialDisplayName || "");

  useEffect(() => {
    if (initialDisplayName) {
      setDisplayValue(initialDisplayName);
    }
  }, [initialDisplayName]);

  useEffect(() => {
    if (!value) {
      setDisplayValue("");
    }
  }, [value]);

  const handleSearch = (searchedValue) => {
    if (!searchedValue?.trim()) {
      setOptions([]);
      return;
    }

    getPractitionerSearchApi(
      `query=${searchedValue}&facility=${facilityId}&role=practitioners`
    )
      .then((res) => {
        setOptions(buildSearchOptions(res?.data, true));
      })
      .catch((err) => console.log(err));
  };

  const optimizedSearch = useCallback(debounce(handleSearch, 300), [
    facilityId,
  ]);

  return (
    <AutoComplete
      options={options}
      className="practitioner-field"
      onSearch={optimizedSearch}
      value={displayValue}
      onSelect={(text, option) => {
        setDisplayValue(text);
        onChange?.(option?.key ?? null);
      }}
      onChange={(text) => {
        setDisplayValue(text);
        if (!text?.trim()) {
          onChange?.(undefined);
        }
      }}
      style={{ width: "100%" }}
    >
      <Input
        size="middle"
        prefix={<SearchOutlined />}
        placeholder="Search Practitioner"
        style={{ width: "100%" }}
      />
    </AutoComplete>
  );
};

const PractitionerSearch = ({
  setSelectedPractitioner,
  practitionerText,
  setPractitionerText,
  facilityId,
  type,
}) => {
  const [data, setData] = useState(null);

  const handleSearch = (searchedValue) => {
    getPractitionerSearchApi(
      `query=${searchedValue}&facility=${facilityId}&role=practitioners`
    )
      .then((res) => {
        setData(buildSearchOptions(res?.data, type));
      })
      .catch((err) => console.log(err));
  };

  const optimizedSearch = useCallback(debounce(handleSearch, 300), [
    facilityId,
  ]);

  const handleSelect = (value, option) => {
    setPractitionerText(value);
    setSelectedPractitioner(option?.key);
  };

  const fieldLabel = type || "Choose Practitioner";

  return (
    <Form.Item
      name="name"
      label={fieldLabel}
      rules={validateConfig.isRequired("Practitioner")}
    >
      <AutoComplete
        options={data}
        className="search"
        onSearch={optimizedSearch}
        onSelect={handleSelect}
        onChange={(value) => {
          setPractitionerText(value);
          if (!value) {
            setSelectedPractitioner?.(null);
          }
          optimizedSearch(value);
        }}
        value={practitionerText}
        style={{ width: "100%" }}
      >
        <Input
          size="middle"
          prefix={<SearchOutlined />}
          placeholder="Search Practitioner"
          className={inputStyles.mdInput}
          style={{ width: "100%" }}
        />
      </AutoComplete>
    </Form.Item>
  );
};

export default PractitionerSearch;
